/**
 * Stem Estimation Enrichment
 *
 * Computes flat stem estimation fields from hexagon detection data and writes them
 * onto the hexagon metadata object. This allows the matrix filter classification
 * pipeline to classify hexagons by stem count or density without any changes to
 * `getPropertyValue` or `classifyWithMatrixFilter`.
 *
 * Data flow:
 *   Backend returns → hexagon.detections[className].estimated_stems: {low, medium, high} | null
 *   This module →    hexagon.metadata.stems_low, stems_medium, stems_high (totals)
 *                    hexagon.metadata.stems_per_ha_low, stems_per_ha_medium, stems_per_ha_high
 *                    hexagon.metadata["stems_low:<className>"] (per-class)
 *                    hexagon.metadata["stems_per_ha_low:<className>"] (per-class)
 *
 * Key decisions:
 *   - If estimated_stems is null for a class → that class is skipped (not eligible)
 *   - If estimated_stems is {low: 0, medium: 0, high: 0} → written as 0 (eligible, zero stems)
 *   - If ALL classes have null estimated_stems → returns empty object (hexagon stays unclassified)
 *   - If area <= 0 → per-ha fields are omitted (cannot compute density)
 */

import { SQUARE_METERS_PER_HECTARE } from '../measurementSystem';

const LEVELS = ['low', 'medium', 'high'];

/**
 * Computes flat stem estimation fields from a hexagon's detections.
 *
 * @param {Object} detections - The hexagon's detections object keyed by class name.
 *   Each value may contain `estimated_stems: {low, medium, high} | null`.
 * @param {number} area - The hexagon area in m².
 * @returns {Object} Flat key-value pairs to merge onto hexagon metadata.
 *   Returns an empty object if no class has stem data.
 */
export function enrichStemEstimates(detections, area) {
	if (!detections || typeof detections !== 'object') return {};

	const totals = { low: 0, medium: 0, high: 0 };
	const result = {};
	let hasAnyStems = false;

	for (const [className, classData] of Object.entries(detections)) {
		const stems = classData?.estimated_stems;
		if (stems == null) continue;

		hasAnyStems = true;

		// Write per-class absolute counts
		for (const level of LEVELS) {
			const value = stems[level] ?? 0;
			totals[level] += value;
			result[`stems_${level}:${className}`] = value;
		}

		// Write per-class per-ha values (only if area is valid)
		if (area > 0) {
			const hectares = area / SQUARE_METERS_PER_HECTARE;
			for (const level of LEVELS) {
				const value = stems[level] ?? 0;
				result[`stems_per_ha_${level}:${className}`] = Math.round(value / hectares);
			}
		}
	}

	// If no class had stem data, return empty — hexagon stays unclassified for stem properties
	if (!hasAnyStems) return {};

	// Write totals
	for (const level of LEVELS) {
		result[`stems_${level}`] = totals[level];
	}

	// Write total per-ha values
	if (area > 0) {
		const hectares = area / SQUARE_METERS_PER_HECTARE;
		for (const level of LEVELS) {
			result[`stems_per_ha_${level}`] = Math.round(totals[level] / hectares);
		}
	}

	return result;
}
